import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { RouterModule } from '@angular/router';
import { TwoColumnPageLayoutComponent } from '@lb/components/page-templates/two-column-page-layout/two-column-page-layout.component';
import { MDButtonGroupComponent, MDButtonToggleComponent, MDTagComponent } from '@melodic-ui/components';
import { ComponentLoaderService } from '@melodic-ui/services';
import { PageHeaderComponent } from '@lb/components/page-header/page-header.component';
import { pageScroll } from '@lb/functions/page-scroll.function';
import { PaginatorComponent } from '@lb/components/paginator/paginator.component';
import { LoaderComponent } from '@lb/components/loader/loader.component';
import { SearchBarComponent } from '@lb/components/search-bar/search-bar.component';
import { NotificationsComponent } from '@lb/components/notifications/notifications.component';
import { ReadonlyLessonCardComponent } from '@lb/components/lessons/readonly-lesson-card/readonly-lesson-card.component';
import { LearnerLessonCardComponent } from '../enrollments/learner-lesson-card.component';
import { LearnerUserStateService } from '../../shared/state/learner-users-state.service';
import { NotificationsStateService } from '@lb/state/notifications-state.service';
import { NeedHelpCardComponent } from '@lb/components';
import { ILessonListItem } from '@lb/api/mylb/interfaces';
import { LessonListService, LessonListTab } from '@lb/components/lessons/lesson-list.service';
import { UserStateService } from '@lb/state/user-state.service';
import { APP_CONFIG, IAppSettings } from '@lb/config/interfaces/iapp-settings.interface';
import { usesLessonDetailsV2 } from '@lb/functions';

@Component({
	selector: 'lrnr-lessons',
	standalone: true,
	imports: [
		RouterModule,
		TwoColumnPageLayoutComponent,
		MDTagComponent,
		MDButtonGroupComponent,
		MDButtonToggleComponent,
		ReadonlyLessonCardComponent,
		LearnerLessonCardComponent,
		PaginatorComponent,
		LoaderComponent,
		SearchBarComponent,
		NotificationsComponent,
		NeedHelpCardComponent
	],
	providers: [LessonListService],
	templateUrl: './lessons.component.html',
	styleUrl: './lessons.component.scss'
})
export class LessonsComponent implements OnInit, OnDestroy {
	private _componentLoaderService: ComponentLoaderService = inject(ComponentLoaderService);
	private _notificationStateService = inject(NotificationsStateService);
	private _learnerUserStateService = inject(LearnerUserStateService);
	private _userStateService = inject(UserStateService);
	private readonly _appSettings = inject<IAppSettings>(APP_CONFIG);

	/** Tab state, paging and fetching for this list. See {@link LessonListService}. */
	protected readonly lessonList = inject(LessonListService);

	protected readonly usesV2 = (lesson: ILessonListItem) => usesLessonDetailsV2(lesson, this._appSettings);

	constructor() {
		this.lessonList.configure({
			userType: 'learner',
			userID: this._learnerUserStateService.activeLearnerUserID,
			searchFields: ['instructorFullName', 'languageName', 'lessonTypeName', 'lessonDate'],
			onItemsLoaded: (lessons) => this._loadUserSummaries(lessons)
		});
	}

	ngOnInit(): void {
		this._componentLoaderService.getContainer('banner-main-content').component.set(PageHeaderComponent);
		this._componentLoaderService.getContainer('banner-main-content').inputs.set({ pageTitle: `My Lessons` });

		this._notificationStateService.loadNotifications(true);
	}

	ngOnDestroy(): void {
		this._componentLoaderService.getContainer('banner-main-content').component.set(null);
	}

	updateSearchValue(event: string | null): void {
		this.lessonList.setSearchText(event);
	}

	updateDisplayType(type: any): void {
		if (type?.[0]) {
			this.lessonList.setDisplayType(type[0] as LessonListTab);
		}
	}

	reload(): void {
		this.lessonList.reload();

		// A reschedule or attendance change can raise a notification, so refresh that panel too.
		this._notificationStateService.loadNotifications(true);
	}

	onPageChange(page: number): void {
		this.lessonList.goToPage(page);
		pageScroll();
	}

	onPageLengthChange(length: number): void {
		this.lessonList.setPageSize(length);
		pageScroll();
	}

	private _loadUserSummaries(lessons: ILessonListItem[]): void {
		const userIDs = lessons.map((lesson) => [lesson.instructorUserID, lesson.learnerUserID]).flat();

		this._userStateService.loadUserSummaries(
			userIDs.filter((id) => id !== null && id !== undefined),
			true
		);
	}
}
